import { useState, useEffect, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";

// All static searchable items (categories + subcategories)
const STATIC_ITEMS: SearchResult[] = [
  // Categories
  { type: "category", label: "Infissi & Serramenti", path: "/infissi", keywords: ["infissi", "serramenti", "finestre", "porte"] },
  { type: "category", label: "Outdoor & Soluzioni Ombra", path: "/outdoor", keywords: ["outdoor", "ombra", "esterno", "giardino"] },
  { type: "category", label: "Ferro & Sicurezza", path: "/ferro", keywords: ["ferro", "sicurezza", "carpenteria", "metallo"] },
  // Infissi subcategories
  { type: "subcategory", label: "Finestre in alluminio", path: "/infissi/finestre-alluminio", keywords: ["finestre", "alluminio", "infissi", "taglio termico"] },
  { type: "subcategory", label: "Porte scorrevoli", path: "/infissi/porte-scorrevoli", keywords: ["porte", "scorrevoli", "vetrate", "infissi"] },
  { type: "subcategory", label: "Persiane e avvolgibili", path: "/infissi/persiane-avvolgibili", keywords: ["persiane", "avvolgibili", "oscuramento", "infissi"] },
  { type: "subcategory", label: "Porte blindate", path: "/infissi/porte-blindate", keywords: ["porte", "blindate", "sicurezza", "infissi"] },
  { type: "subcategory", label: "Zanzariere", path: "/infissi/zanzariere", keywords: ["zanzariere", "zanzara", "infissi"] },
  { type: "subcategory", label: "Finestre in legno", path: "/infissi/finestre-legno", keywords: ["finestre", "legno", "infissi", "tradizione"] },
  // Outdoor subcategories
  { type: "subcategory", label: "Pergole bioclimatiche", path: "/outdoor/pergole", keywords: ["pergole", "bioclimatiche", "lamelle", "outdoor"] },
  { type: "subcategory", label: "Tende da sole", path: "/outdoor/tende-sole", keywords: ["tende", "sole", "tenda", "outdoor"] },
  { type: "subcategory", label: "Gazebi", path: "/outdoor/gazebi", keywords: ["gazebi", "gazebo", "outdoor"] },
  { type: "subcategory", label: "Tettoie", path: "/outdoor/tettoie", keywords: ["tettoie", "tettoia", "copertura", "outdoor"] },
  { type: "subcategory", label: "Arredi esterni", path: "/outdoor/arredi", keywords: ["arredi", "arredo", "esterni", "outdoor"] },
  { type: "subcategory", label: "Chiusure verticali", path: "/outdoor/chiusure", keywords: ["chiusure", "verticali", "outdoor"] },
  // Ferro subcategories
  { type: "subcategory", label: "Cancelli", path: "/ferro/cancelli", keywords: ["cancelli", "cancello", "ingresso", "ferro"] },
  { type: "subcategory", label: "Ringhiere e balconi", path: "/ferro/ringhiere", keywords: ["ringhiere", "ringhiera", "balconi", "ferro"] },
  { type: "subcategory", label: "Grate di sicurezza", path: "/ferro/grate", keywords: ["grate", "grata", "inferriate", "sicurezza", "ferro"] },
  { type: "subcategory", label: "Scale", path: "/ferro/scale", keywords: ["scale", "scala", "ferro"] },
  { type: "subcategory", label: "Recinzioni", path: "/ferro/recinzioni", keywords: ["recinzioni", "recinzione", "ferro"] },
  { type: "subcategory", label: "Balconate", path: "/ferro/balconate", keywords: ["balconate", "balconata", "ferro battuto", "ferro"] },
];

export interface SearchResult {
  type: "category" | "subcategory" | "project";
  label: string;
  path: string;
  keywords: string[];
  description?: string;
}

// Levenshtein distance for fuzzy matching
function levenshtein(a: string, b: string): number {
  const m = a.length, n = b.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] = a[i - 1] === b[j - 1]
        ? dp[i - 1][j - 1]
        : 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
    }
  }
  return dp[m][n];
}

function fuzzyMatch(query: string, target: string): number {
  const q = query.toLowerCase().trim();
  const t = target.toLowerCase();
  
  // Exact substring match = best score
  if (t.includes(q)) return 0;
  // Starts with
  if (t.startsWith(q)) return 0;
  
  // Check individual words
  const words = t.split(/\s+/);
  for (const word of words) {
    if (word.startsWith(q)) return 0.5;
    if (q.length >= 3 && levenshtein(q, word) <= Math.floor(q.length / 3)) return 1;
  }
  
  // Levenshtein on full string (for short queries)
  if (q.length >= 3) {
    const dist = levenshtein(q, t.slice(0, Math.min(q.length + 2, t.length)));
    if (dist <= Math.floor(q.length / 3)) return 1.5;
  }
  
  return Infinity;
}

// Map DB category keys to paths
const CATEGORY_PATH_MAP: Record<string, string> = {
  "infissi-finestre-alluminio": "/infissi/finestre-alluminio",
  "infissi-porte-scorrevoli": "/infissi/porte-scorrevoli",
  "infissi-persiane-avvolgibili": "/infissi/persiane-avvolgibili",
  "infissi-porte-blindate": "/infissi/porte-blindate",
  "infissi-zanzariere": "/infissi/zanzariere",
  "infissi-finestre-legno": "/infissi/finestre-legno",
  "outdoor-pergole": "/outdoor/pergole",
  "outdoor-tende-sole": "/outdoor/tende-sole",
  "outdoor-gazebi": "/outdoor/gazebi",
  "outdoor-tettoie": "/outdoor/tettoie",
  "outdoor-arredi": "/outdoor/arredi",
  "outdoor-chiusure": "/outdoor/chiusure",
  "ferro-cancelli": "/ferro/cancelli",
  "ferro-ringhiere": "/ferro/ringhiere",
  "ferro-grate": "/ferro/grate",
  "ferro-scale": "/ferro/scale",
  "ferro-recinzioni": "/ferro/recinzioni",
  "ferro-balconate": "/ferro/balconate",
};

export const useSearch = (query: string) => {
  const [dbProjects, setDbProjects] = useState<SearchResult[]>([]);

  // Fetch all project titles once
  useEffect(() => {
    const fetch = async () => {
      const { data } = await supabase
        .from("portfolio_projects")
        .select("title, description, category")
        .order("created_at", { ascending: false });
      
      if (data) {
        setDbProjects(
          data.map((p) => ({
            type: "project" as const,
            label: p.title,
            path: CATEGORY_PATH_MAP[p.category] || "/",
            keywords: [p.title.toLowerCase(), ...(p.description || "").toLowerCase().split(/\s+/)],
            description: p.description || undefined,
          }))
        );
      }
    };
    fetch();
  }, []);

  const results = useMemo(() => {
    if (!query || query.trim().length < 2) return [];

    const allItems = [...STATIC_ITEMS, ...dbProjects];
    const scored: { item: SearchResult; score: number }[] = [];

    for (const item of allItems) {
      // Check label
      let bestScore = fuzzyMatch(query, item.label);
      // Check keywords
      for (const kw of item.keywords) {
        const s = fuzzyMatch(query, kw);
        if (s < bestScore) bestScore = s;
      }
      if (bestScore < Infinity) {
        // Boost categories over subcategories over projects
        const typeBoost = item.type === "category" ? -0.1 : item.type === "subcategory" ? 0 : 0.1;
        scored.push({ item, score: bestScore + typeBoost });
      }
    }

    scored.sort((a, b) => a.score - b.score);

    // Deduplicate by path
    const seen = new Set<string>();
    return scored
      .filter(({ item }) => {
        if (seen.has(item.path)) return false;
        seen.add(item.path);
        return true;
      })
      .slice(0, 8)
      .map(({ item }) => item);
  }, [query, dbProjects]);

  return results;
};
